import { useMutation, useQuery } from '@vue/apollo-composable';
import { storeToRefs } from 'pinia';
import { computed, type Ref, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useLocationsStore } from '@/stores/locations';
import { GET_GIFTY, UPDATE_GIFTY_LOCATIONS } from './graphql';
import type {
  GiftyLocation,
  GiftyLocationMapping,
  LocationMappings,
} from './types';

export const useLocations = (connected: Ref<boolean>) => {
  const { t } = useI18n();
  const { locations, locationIds } = storeToRefs(useLocationsStore());

  const locationMappings = ref<LocationMappings>({});
  const saveLocationsFailed = ref(false);

  watch(
    locationMappings,
    () => {
      saveLocationsFailed.value = false;
    },
    { deep: true },
  );

  const salonLocations = computed(() =>
    locations.value.filter((location) =>
      locationIds.value.includes(location.id),
    ),
  );

  const { result, onResult, refetch } = useQuery(GET_GIFTY);

  const giftyLocations = computed<GiftyLocation[]>(
    () => result.value?.gifty?.locations || [],
  );

  const giftyLocationOptions = computed(() => [
    { value: null, label: t('integrations.gifty.settings.no_location') },
    ...giftyLocations.value.map((location) => ({
      value: location.id,
      label: location.address,
    })),
  ]);

  const needsLocations = (gifty?: {
    locations: GiftyLocation[];
    locationMappings: GiftyLocationMapping[];
  }) => {
    const giftyCount = gifty?.locations.length || 0;
    const autoLinked =
      giftyCount === 1 &&
      salonLocations.value.length === 1 &&
      !!gifty?.locationMappings.some(
        (mapping) => mapping.locationId === salonLocations.value[0].id,
      );

    return giftyCount > 0 && !autoLinked;
  };

  const showLocations = computed(
    () => connected.value && needsLocations(result.value?.gifty),
  );

  onResult(({ data }) => {
    const mappings: GiftyLocationMapping[] =
      data?.gifty?.locationMappings || [];

    locationMappings.value = Object.fromEntries(
      salonLocations.value.map((location) => [
        location.id,
        mappings.find((mapping) => mapping.locationId === location.id)
          ?.giftyLocationId || null,
      ]),
    );
  });

  const refetchLocations = (): Promise<boolean> =>
    refetch()?.then(({ data }) => needsLocations(data?.gifty)) ??
    Promise.resolve(false);

  const { mutate: updateLocations, loading: savingLocations } = useMutation(
    UPDATE_GIFTY_LOCATIONS,
  );

  const saveLocations = (): Promise<boolean> =>
    updateLocations({
      input: {
        locationMappings: salonLocations.value.map((location) => ({
          locationId: location.id,
          giftyLocationId: locationMappings.value[location.id] || null,
        })),
      },
    })
      .then((response) => {
        if (response?.data?.updateGiftyLocations?.errors?.length) {
          saveLocationsFailed.value = true;
          return false;
        }

        return true;
      })
      .catch(() => {
        saveLocationsFailed.value = true;
        return false;
      });

  return {
    giftyLocationOptions,
    locationMappings,
    salonLocations,
    saveLocationsFailed,
    savingLocations,
    showLocations,
    refetchLocations,
    saveLocations,
  };
};
